import React, { useState, useRef, useEffect } from "react";
import { Upload, Download } from 'lucide-react';
import { Toast } from "primereact/toast";
import { ClimbingBoxLoader } from "react-spinners";
import API_BASE_URL from "../../Config";

const EmployeeMaster = () => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [entityList, setEntityList] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState("");
  const [loading, setLoading] = useState(false);
  const toast = useRef(null);
  const fileInputRef = useRef(null);

  // Extract user data
  const roleId = parseInt(sessionStorage.getItem("user_role_id"), 10);
  const clientId = sessionStorage.getItem("client_id");
  const username = sessionStorage.getItem("current_username");
  const companyName = sessionStorage.getItem("company_name");
  const token = sessionStorage.getItem("token");

  const isUploader = roleId === 2;

  // Fetch entities if superadmin
  useEffect(() => {
    if (roleId === 1) {
      fetch(`${API_BASE_URL}api/overall_clients/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("Client API response:", data);
          if (data.data && Array.isArray(data.data.client)) {
            setEntityList(data.data.client);
          } else {
            setEntityList([]);
          }
        })
        .catch(() => {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to load clients.",
            life: 0, // Never auto-hide
            closable: true,
            sticky: true,
          });
        });
    }
  }, [roleId, token]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && /\.(xlsx|xls)$/i.test(file.name)) {
      setUploadedFile(file);
    } else {
      toast.current?.show({
        severity: "warn",
        summary: "Invalid File",
        detail: "Only .xlsx or .xls files are allowed.",
        life: 0, // Never auto-hide
        closable: true,
        sticky: true,
      });
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownload = async () => {
    const token = sessionStorage.getItem("token");
    const roleId = sessionStorage.getItem("user_role_id");
    const username = sessionStorage.getItem("current_username");
    const companyName =
      roleId === "1" ? selectedEntity : sessionStorage.getItem("company_name");

    if (!companyName) {
      toast.current?.show({
        severity: "error",
        summary: "Company Missing",
        detail: "Please select or log in with a valid company.",
        life: 0, // Never auto-hide
        closable: true,
        sticky: true,
      });
      return;
    }

    if (roleId === "3" && !username) {
      toast.current?.show({
        severity: "error",
        summary: "Missing Username",
        detail: "Username is required for role 3 to download.",
        life: 0, // Never auto-hide
        closable: true,
        sticky: true,
      });
      return;
    }

    setLoading(true);

    try {
      let query = `company_name=${encodeURIComponent(companyName)}&role_id=${roleId}`;
      if (roleId === "3") {
        query += `&username=${encodeURIComponent(username)}`;
      }

      const res = await fetch(`${API_BASE_URL}api/download_employee_master/?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        toast.current?.show({
          severity: "warn",
          summary: "Download Failed",
          detail:
            res.status === 404
              ? "Employee Master not available for this company."
              : "Failed to download file.",
          life: 0, // Never auto-hide
          closable: true,
          sticky: true,
        });
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Extract filename from Content-Disposition header

      let filename = "EmployeeMaster.xlsx" // Default fallback

      const contentDisposition = res.headers.get("Content-Disposition")

      if (contentDisposition) {

        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)

        if (filenameMatch && filenameMatch[1]) {

          filename = filenameMatch[1].replace(/['"]/g, "")

        }

      }

      link.setAttribute("download", filename) // Changed this line
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.current?.show({
        severity: "success",
        summary: "Download Complete",
        detail: "Employee Master downloaded successfully!",
        life: 6000,
        closable: true,
        
      });
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Download Error",
        detail: error.message,
        life: 0, // Never auto-hide
        closable: true,
        sticky: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!uploadedFile) {
      toast.current?.show({
        severity: "warn",
        summary: "Upload Required",
        detail: "Please upload a file before submitting.",
        life: 0, // Never auto-hide
        closable: true,
        sticky: true,
      });
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("employee_master", uploadedFile);
    formData.append("client_id", clientId);
    formData.append("username", username || "");
    formData.append("company_name", companyName || "");
    formData.append("role_id", roleId);
    if (roleId === 1) formData.append("entity_id", selectedEntity);

    try {
      const response = await fetch(`${API_BASE_URL}api/upload_employee_master/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const contentDisposition = response.headers.get("Content-Disposition");
      const contentType = response.headers.get("Content-Type");
      
      // Check if response is a file (error file download)
      const isFile = (contentDisposition && contentDisposition.includes("attachment")) || 
        (contentType && (
          contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
          contentType.includes("application/vnd.ms-excel") ||
          contentType.includes("application/octet-stream")
        ));

      if (isFile) {
        // Backend returned an error file — download it
        const blob = await response.blob();
        
        // Extract exact filename from Content-Disposition header
        let filename = "error_file.xlsx"; // default fallback
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, "");
          }
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        
        // Clear the uploaded file
        handleRemoveFile();

        toast.current?.show({
          severity: "error",
          summary: "Upload Failed - Error File Downloaded",
         // detail: `Upload failed with validation errors. Error file "${filename}" has been downloaded automatically. Please check the file for details and correct the issues.`,
          life: 0, // Never auto-hide
          closable: true,
          sticky: true,
        });
      } else if (response.ok) {
        // Successful upload without error file
        const data = await response.json();
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: data?.message || "File uploaded successfully.",
          life: 0, // Never auto-hide
          closable: true,
          sticky: true,
        });
        handleRemoveFile();
        setSelectedEntity("");
      } else {
        // Regular error response
        const data = await response.json();
        
        // Clear the uploaded file on any error
        handleRemoveFile();
        
        toast.current?.show({
          severity: "error",
          summary: `Upload Failed (${response.status})`,
          detail: data?.message || "An error occurred.",
          life: 0, // Never auto-hide
          closable: true,
          sticky: true,
        });
      }
    } catch (err) {
      // Clear the uploaded file on any error
      handleRemoveFile();
      
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.message,
        life: 0, // Never auto-hide
        closable: true,
        sticky: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    handleRemoveFile();
    if (roleId === 1) setSelectedEntity("");
  };

  return (
    <div className="relative">
      {/* Loader Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <ClimbingBoxLoader color="#d97925" size={20} />
        </div>
      )}
      <div
        className="flex flex-col items-center justify-center  bg-cover bg-center p-4 text-white"
        style={{ backgroundImage: "url('/background.jpg')" }}
      >
        <Toast ref={toast} />
        <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl shadow-lg p-8 w-full max-w-md">
          <h2 className="text-2xl font-bold mb-6 text-center">
            Employee Master Configuration
          </h2>

          {/* Entity Dropdown for Superadmin */}
          {roleId === 1 && (
            <div className="mb-6">
              <label htmlFor="entity" className="block mb-2 text-sm">
                
              </label>
              <select
                id="entity"
                value={selectedEntity}
                onChange={(e) => setSelectedEntity(e.target.value)}
                className="w-full px-4 py-2 rounded-md bg-white/10 border border-white/20 text-black focus:outline-none focus:ring-2 focus:ring-[#d97925]"
              >
                <option value="">-- Select Entity --</option>
                {entityList.map((entity, index) => (
                  <option key={index} value={entity}>
                    {entity}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Download Button */}
          <div className="flex flex-col items-center mb-6">
            <button
              onClick={handleDownload}
              className="text-[#d97925] hover:text-white bg-white hover:bg-[#d97925] border border-[#d97925] rounded-full p-3 transition-all"
            >
              <Download size={20} />
            </button>
            <span className="mt-2 text-sm">Download Employees</span>
          </div>

          {/* Upload Section for Uploader */}
          {isUploader && (
            <>
              <div className="flex flex-col items-center mb-4">
                <label className="cursor-pointer">
                  <div className="text-[#d97925] hover:text-white bg-white hover:bg-[#d97925] border border-[#d97925] rounded-full p-3 transition-all flex items-center justify-center">
                    <Upload size={20} />
                    <input
                      type="file"
                      accept=".xlsx"
                      onChange={handleFileChange}
                      className="hidden"
                      ref={fileInputRef}
                    />
                  </div>
                </label>
                <span className="mt-2 text-sm">Upload Document (.xlsx)</span>
              </div>

              {/* Uploaded File Preview */}
              {uploadedFile && (
                <div className="flex justify-between items-center bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-4 text-sm text-white">
                  <span className="truncate">{uploadedFile.name}</span>
                  <button
                    onClick={handleRemoveFile}
                    className="text-red-400 hover:text-red-600 ml-4 text-sm"
                  >
                    Remove
                  </button>
                </div>
              )}

              {/* Submit and Clear Buttons */}
              <div className="flex gap-4 justify-center mt-4">
                <button
                  onClick={handleSubmit}
                  className="bg-[#d97925] hover:bg-white hover:text-[#d97925] text-white px-4 py-2 rounded-full border border-[#d97925] transition-all"
                >
                  Submit
                </button>
                <button
                  onClick={handleClear}
                  className="bg-white hover:bg-[#d97925] hover:text-white text-[#d97925] px-4 py-2 rounded-full border border-[#d97925] transition-all"
                >
                  Clear
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeMaster;